import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { useLogin } from '../lib/session';
import { useI18n } from '../lib/i18n';
import { Alert, Button, Field, LanguagePicker, Logo } from '../components/ui';

interface FormValues { email: string; password: string; code?: string }

export function LoginPage() {
  const { t } = useI18n();
  const login = useLogin();

  /*
   * The code field appears only once the server has asked for it, and then it
   * stays: a wrong code comes back as "email or password is incorrect" — the
   * server does not say which half failed — and hiding the field on that reply
   * would send the person back to retyping their password.
   */
  const [askCode, setAskCode] = useState(false);

  // Built here rather than at module level: the messages are translated, and a
  // schema created once at import time would be stuck in whichever language
  // was active when the bundle loaded.
  const schema = z.object({
    email: z.string().min(1, t('login.emailRequired')).email(t('login.emailInvalid')),
    password: z.string().min(1, t('login.passwordRequired')),
    code: askCode
      ? z.string().trim().min(6, t('login.codeRequired'))
      : z.string().trim().optional(),
  });

  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', code: '' },
  });

  const errorText = useApiError();
  const error = login.error instanceof ApiError ? login.error : null;

  // The code's own 403 is not a failure to report: the field below is the
  // answer to it, and an error box above a freshly appeared field reads as
  // though something went wrong.
  const failure = error && error.code !== 'TWO_FACTOR_REQUIRED' ? error : null;

  return (
    <main className="grid min-h-dvh place-items-center px-5 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-[max(2.5rem,env(safe-area-inset-top))]">
      <div className="w-full max-w-sm">
        <div className="mb-9 flex flex-col items-center gap-5 text-center">
          <Logo withWordmark size="lg" />
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink">{t('login.title')}</h1>
          </div>
        </div>

        <form
          noValidate
          onSubmit={handleSubmit(({ email, password, code }) => login.mutate(
            // An empty string would fail the server's `min(6)`; omit it instead.
            code && code.length > 0 ? { email, password, code } : { email, password },
            { onError: (reason) => {
              if (reason instanceof ApiError && reason.code === 'TWO_FACTOR_REQUIRED') {
                setAskCode(true);
              }
            } },
          ))}
          className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-6"
        >
          {failure && (
            <Alert tone={failure.code === 'ACCOUNT_LOCKED' ? 'warning' : 'danger'}>
              {errorText(failure) ?? failure.message}
              {failure.code === 'RATE_LIMITED' && (
                <span className="mt-1 block text-xs text-ink-dim">{t('login.waitBeforeRetry')}</span>
              )}
            </Alert>
          )}

          {askCode && !failure && <Alert tone="info">{t('login.codeAsked')}</Alert>}

          <Field
            label={t('login.email')}
            type="email"
            autoComplete="username"
            autoFocus
            error={formState.errors.email?.message}
            {...register('email')}
          />
          <Field
            label={t('login.password')}
            type="password"
            autoComplete="current-password"
            error={formState.errors.password?.message}
            {...register('password')}
          />
          {askCode && (
            <Field
              label={t('login.code')}
              inputMode="numeric"
              autoComplete="one-time-code"
              // Mounts the moment the server asks, so this lands the cursor there.
              autoFocus
              error={formState.errors.code?.message}
              {...register('code')}
            />
          )}

          <Button type="submit" loading={login.isPending} className="mt-1">
            {t('login.submit')}
          </Button>
        </form>

        <LanguagePicker className="mt-4 justify-center" />

        <p className="mt-4 text-center text-xs text-ink-faint">
          {t('login.footer')}
        </p>
      </div>
    </main>
  );
}
